import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Gauge, Leaf, ShieldCheck, Sun, Truck } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Marquee from "../components/Marquee";
import SEO, { SITE_URL } from "../components/SEO";
import { EASE, Reveal, SectionTag, money } from "../components/bits";

const HERO_LINES = ["HARVEST", "BEYOND", "LIMITS."];

function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [24, -24]);
  const imgY = useTransform(sy, [-0.5, 0.5], [18, -18]);
  const glowX = useTransform(sx, [-0.5, 0.5], [-70, 70]);
  const glowY = useTransform(sy, [-0.5, 0.5], [-50, 50]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <motion.div
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2.2, ease: EASE }}
        className="pointer-events-none absolute inset-0"
        data-testid="hero-backdrop"
      >
        <img
          src="/img/brand/hero-wall.jpg"
          alt=""
          className="absolute left-[62%] top-[47%] max-h-[62vh] w-auto max-w-[92vw] -translate-x-1/2 -translate-y-1/2 opacity-75 lg:max-w-[54vw]"
          style={{
            maskImage: "radial-gradient(ellipse at center, black 48%, transparent 72%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 48%, transparent 72%)",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,11,7,0.95) 0%, rgba(5,11,7,0.72) 42%, rgba(5,11,7,0.35) 70%, rgba(5,11,7,0.5) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,11,7,0.5) 0%, transparent 30%, transparent 68%, #050B07 100%)" }} />
      </motion.div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(1000px circle at 78% 30%, rgba(57,255,20,0.10), transparent 55%), radial-gradient(700px circle at 10% 90%, rgba(16,185,129,0.08), transparent 55%)" }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: "linear-gradient(rgba(57,255,20,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.35) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at 70% 30%, black 20%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at 70% 30%, black 20%, transparent 70%)",
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#39FF14]/25 bg-[#0A150F]/80 px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] animate-pulse-ring" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#86EFAC]">
              LED grow lights · Designed in Australia
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                  className={`block ${i === 1 ? "text-hollow" : "text-[#F0FDF4]"} ${i === 2 ? "text-[#39FF14] text-glow-lime" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-6 max-w-lg text-base leading-relaxed text-[#9FB8A8] sm:text-lg"
          >
            Precision LED grow lights with Samsung and Cree diodes, for Australian home growers and commercial use.
            Bigger yields, whisper-quiet rigs, and power bills that finally make sense.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group flex items-center gap-2 rounded-full bg-[#39FF14] px-7 py-3.5 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_36px_rgba(57,255,20,0.5)]"
            >
              Shop the range
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-savings-button"
              onClick={() => document.getElementById("savings")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border border-[#39FF14]/30 px-7 py-3.5 font-display text-sm font-bold text-[#F0FDF4] transition-colors hover:border-[#39FF14]/70 hover:text-[#39FF14]"
            >
              See the savings
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {["Samsung & Cree diodes", "2-year local warranty", "Free AU express shipping"].map((b) => (
              <span key={b} className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#4ADE80]">
                <ShieldCheck className="h-3.5 w-3.5" />{` ${b}`}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative hidden lg:block"
        >
          <motion.div
            className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ x: glowX, y: glowY, background: "radial-gradient(circle, rgba(57,255,20,0.22), transparent 60%)" }}
          />
          <div className="absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#39FF14]/15" />
          <div className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#39FF14]/8" />
          <motion.div style={{ x: imgX, y: imgY }} className="animate-float-slow relative">
            <div className="overflow-hidden rounded-3xl border border-[#39FF14]/25 bg-[#071009] shadow-[0_40px_120px_-20px_rgba(57,255,20,0.25)]">
              <img src="/img/products/galaxy.png" alt="Galaxy 750 LED grow light" className="h-[420px] w-full object-contain p-8" />
            </div>
            <div className="glass absolute -left-6 top-8 rounded-xl px-4 py-3">
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Galaxy 750</div>
              <div className="font-display text-lg font-bold text-[#F0FDF4]">2.9 µmol/J</div>
            </div>
            <div className="glass absolute -right-4 bottom-10 rounded-xl px-4 py-3">
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Bloom coverage</div>
              <div className="font-display text-lg font-bold text-[#F0FDF4]">1.5 × 1.5 m</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.img
        src="/img/brand/alien.png"
        alt="SuperTechLED alien mascot"
        data-testid="hero-alien"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.4, ease: EASE }}
        className="animate-float-slow pointer-events-none absolute -left-4 bottom-16 hidden h-44 w-auto drop-shadow-[0_0_44px_rgba(57,255,20,0.35)] xl:block"
      />

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => document.getElementById("range")?.scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#4ADE80]/70"
        aria-label="Scroll down"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
}

const CHAPTERS = [
  {
    n: "01",
    title: "The Spectrum Philosophy",
    icon: Sun,
    body: "Blurple is dead. We tune full-spectrum white to the photosynthetic peaks at 450nm and 660nm, then spike deep red, far-red and UV exactly where plants cash photons in for growth.",
  },
  {
    n: "02",
    title: "Silence & Cool Heads",
    icon: Leaf,
    body: "Zero moving parts. Heavy-gauge anodised heatsinks run up to 15°C cooler than fan-cooled rigs — no drone, no dust, nothing to fail mid-flower.",
  },
  {
    n: "03",
    title: "Built for Australian Homes",
    icon: ShieldCheck,
    body: "SAA & RCM certified electrics, humidity-tough boards, a 2-year local warranty and support from people who actually grow. Free express shipping, every order.",
  },
];

function Manifesto() {
  return (
    <section id="why" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="manifesto-section">
      <Reveal>
        <SectionTag>The manifesto</SectionTag>
        <h2 className="font-display mt-4 max-w-2xl text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Three reasons your plants <span className="text-[#39FF14]">notice the difference</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.12}>
            <div className="spotlight-card group h-full rounded-2xl border border-[#39FF14]/15 bg-[#0A150F] p-7">
              <div className="flex items-start justify-between">
                <span className="font-display text-5xl font-black text-[#39FF14]/15 transition-colors duration-500 group-hover:text-[#39FF14]/40">
                  {c.n}
                </span>
                <c.icon className="h-6 w-6 text-[#39FF14]" />
              </div>
              <h3 className="font-display mt-6 text-xl font-bold text-[#F0FDF4]">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#9FB8A8]">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BrandWall() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1.02]);
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="brand-wall">
      <motion.img
        src="/img/brand/brand-wall.webp"
        alt="SuperTechLED — Grow Lights · Alien Technology"
        loading="lazy"
        style={{ scale, y }}
        className="h-[62vh] w-full object-cover sm:h-[78vh]"
      />
      <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, #050B07 0%, transparent 18%, transparent 72%, #050B07 100%)" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 55%, transparent 30%, rgba(5,11,7,0.55) 100%)" }} />
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-14 text-center">
        <Reveal>
          <div className="font-mono2 text-[10px] uppercase tracking-[0.5em] text-[#F0FDF4]/80">Grow Lights</div>
          <div className="font-brand mt-2 text-2xl tracking-[0.18em] text-white drop-shadow-[0_0_24px_rgba(255,45,120,0.55)] sm:text-4xl">
            ALIEN TECHNOLOGY
          </div>
          <p className="mx-auto mt-3 max-w-md px-6 text-xs text-[#F0FDF4]/70 sm:text-sm">
            Taking your yields to out of this worlds levels
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// Digitised from SuperTechLED's "Original Spectrum" chart (Ref: Medicinal Plant), 380–780nm
const WL = [380, 395, 410, 425, 440, 450, 465, 480, 490, 500, 515, 530, 545, 560, 575, 590, 605, 620, 630, 645, 660, 675, 690, 705, 730, 760, 780];
const ORIGINAL = [0.00, 0.15, 0.04, 0.05, 0.35, 0.75, 0.55, 0.38, 0.35, 0.42, 0.50, 0.55, 0.52, 0.60, 0.70, 0.82, 0.85, 0.78, 0.75, 0.80, 1.00, 0.60, 0.40, 0.28, 0.15, 0.06, 0.02];
const REF_LINE = [0.05, 0.35, 0.75, 0.98, 0.90, 0.82, 0.60, 0.45, 0.35, 0.28, 0.24, 0.22, 0.24, 0.30, 0.37, 0.40, 0.44, 0.50, 0.55, 0.65, 0.80, 0.55, 0.25, 0.05, 0.00, 0.00, 0.00];

const stageWeight = (wl, mode) => {
  if (mode === "veg") return wl <= 500 ? 1.3 : wl >= 600 ? 0.55 : 0.85;
  if (mode === "bloom") return wl <= 480 ? 0.55 : wl >= 600 && wl <= 700 ? 1.15 : 0.9;
  return 1;
};

const SPECTRA = {
  veg: { label: "Veg · blue-weighted", desc: "The Original Spectrum weighted toward the 450nm blue peak — tight internodes and rapid canopy build." },
  bloom: { label: "Bloom · red-weighted", desc: "Weighted onto the 660nm deep-red spike to drive flowering density and final weight." },
  full: { label: "Original Spectrum · Ref: Medicinal Plant", desc: "The measured output of SuperTechLED fixtures, 380–780nm: UV bump, 450nm blue peak, full-body white and the 660nm red spike — plotted against the plant response curve." },
};

const spectrumValues = (mode) => ORIGINAL.map((v, i) => Math.min(1, v * stageWeight(WL[i], mode)));

function smoothPath(wls, values, w = 1000, h = 300) {
  const X = (wl) => ((wl - 380) / 400) * w;
  const Y = (v) => h - v * h * 0.88 - 8;
  const p = values.map((v, i) => [X(wls[i]), Y(v)]);
  let d = `M ${p[0][0]},${p[0][1]}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] || p2;
    d += ` C ${p1[0] + (p2[0] - p0[0]) / 6},${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6},${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]},${p2[1]}`;
  }
  return d;
}

function SpectrumSection() {
  const [mode, setMode] = useState("full");
  const d = smoothPath(WL, spectrumValues(mode));
  const dRef = smoothPath(WL, REF_LINE);
  return (
    <section id="spectrum" className="border-y border-[#39FF14]/10 bg-[#071009]" data-testid="spectrum-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[0.9fr_1.4fr] lg:items-center">
        <Reveal>
          <SectionTag>Light you can see working</SectionTag>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl">
            The Original Spectrum, <span className="text-[#39FF14]">decoded</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
            This is the real measured output of a SuperTechLED fixture, plotted against the plant response curve
            (Ref: Medicinal Plant). Flip between grow stages to see how the light re-weights its photons to match
            what your plants are actually hungry for.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {Object.entries(SPECTRA).map(([key, s]) => (
              <button
                key={key}
                data-testid={`spectrum-mode-${key}`}
                onClick={() => setMode(key)}
                className={`rounded-full px-5 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-all ${
                  mode === key
                    ? "bg-[#39FF14] text-[#050B07] shadow-[0_0_24px_rgba(57,255,20,0.35)]"
                    : "border border-[#39FF14]/25 text-[#86EFAC] hover:border-[#39FF14]/60"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
          <p className="mt-5 text-sm italic text-[#86EFAC]" data-testid="spectrum-mode-description">{SPECTRA[mode].desc}</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#C9DCD0]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#8B5CF6,#22C55E,#EF4444)" }} />
              SuperTechLED output
            </span>
            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#C9DCD0]">
              <span className="h-0.5 w-5 bg-white/90" />
              Plant response · Ref
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="glass rounded-2xl p-6" data-testid="spectrum-chart">
            <div className="mb-3 flex items-baseline justify-between">
              <span className="font-display text-sm font-bold text-[#F0FDF4]">Original Spectrum</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#4ADE80]">Ref.: Medicinal Plant</span>
            </div>
            <svg viewBox="0 0 1000 360" className="w-full">
              <defs>
                <linearGradient id="specfill" x1="0" x2="1">
                  <stop offset="0%" stopColor="#7C3AED" />
                  <stop offset="15%" stopColor="#3B82F6" />
                  <stop offset="30%" stopColor="#22D3EE" />
                  <stop offset="45%" stopColor="#22C55E" />
                  <stop offset="60%" stopColor="#EAB308" />
                  <stop offset="78%" stopColor="#F97316" />
                  <stop offset="92%" stopColor="#EF4444" />
                  <stop offset="100%" stopColor="#B91C1C" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75].map((f) => (
                <line key={f} x1="0" x2="1000" y1={300 * f} y2={300 * f} stroke="rgba(255,255,255,0.07)" strokeDasharray="4 8" />
              ))}
              <motion.path
                initial={{ d: `${d} L 1000,300 L 0,300 Z` }}
                animate={{ d: `${d} L 1000,300 L 0,300 Z` }}
                transition={{ duration: 0.9, ease: EASE }}
                fill="url(#specfill)"
                opacity="0.85"
                stroke="none"
              />
              <motion.path
                initial={{ d }}
                animate={{ d }}
                transition={{ duration: 0.9, ease: EASE }}
                fill="none"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="1.5"
              />
              <path
                d={dRef}
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="3"
                strokeLinecap="round"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.55))" }}
              />
              {[380, 460, 540, 620, 700, 780].map((wl) => (
                <text key={wl} x={((wl - 380) / 400) * 1000} y="330" fill="#4ADE80" fontSize="14" fontFamily="JetBrains Mono" textAnchor="middle">
                  {wl}
                </text>
              ))}
              <text x="500" y="352" fill="#4ADE80" fontSize="12" fontFamily="JetBrains Mono" textAnchor="middle" opacity="0.7">Wavelength (nm)</text>
            </svg>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const RATE = 0.32;
const monthly = (w) => (w * 12 * 30) / 1000 * RATE;

const TENTS = [
  { id: "08", label: "0.8 × 0.8 m", light: "Optic Sun 1 · 56W", slug: "optic-sun-1", led: 56, hps: 150, boost: 41 },
  { id: "10", label: "1.0 × 1.0 m", light: "Quantum Sun QS-2 · 240W", slug: "quantum-sun", led: 240, hps: 400, boost: 32 },
  { id: "12", label: "1.2 × 1.2 m", light: "Supa Nova · 400W", slug: "supa-nova", led: 400, hps: 600, boost: 37 },
  { id: "15", label: "1.5 × 1.5 m", light: "Galaxy 750 · 750W", slug: "galaxy", led: 750, hps: 1000, boost: 33 },
];

function Savings() {
  const [tent, setTent] = useState(TENTS[1]);
  const saveMo = monthly(tent.hps) - monthly(tent.led);
  return (
    <section id="savings" className="mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="savings-section">
      <Reveal>
        <SectionTag>The honest maths</SectionTag>
        <h2 className="font-display mt-4 max-w-2xl text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Old HPS vs SuperTechLED — <span className="text-[#39FF14]">your power bill decides</span>
        </h2>
        <p className="mt-4 max-w-xl text-sm text-[#9FB8A8] sm:text-base">
          Pick your tent size. We compare the recommended SuperTechLED against the old-school HPS it replaces, at
          $0.32/kWh on a 12-hour flower cycle.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.4fr]">
        <Reveal className="flex flex-col gap-3">
          {TENTS.map((t) => (
            <button
              key={t.id}
              data-testid={`tent-option-${t.id}`}
              onClick={() => setTent(t)}
              className={`flex items-center justify-between rounded-2xl border px-6 py-5 text-left transition-all ${
                tent.id === t.id
                  ? "border-[#39FF14]/60 bg-[#0E1C13] shadow-[0_0_30px_rgba(57,255,20,0.12)]"
                  : "border-[#39FF14]/12 bg-[#0A150F] hover:border-[#39FF14]/35"
              }`}
            >
              <div>
                <div className="font-display text-lg font-bold text-[#F0FDF4]">{t.label}</div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#4ADE80]">{t.light}</div>
              </div>
              <Gauge className={`h-5 w-5 ${tent.id === t.id ? "text-[#39FF14]" : "text-[#4ADE80]/40"}`} />
            </button>
          ))}
        </Reveal>
        <Reveal delay={0.1}>
          <div className="glass h-full rounded-2xl p-8">
            <div className="grid gap-6 sm:grid-cols-3">
              <div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Power draw</div>
                <div className="mt-2 space-y-2">
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]"><span>LED</span><span>{`${tent.led}W`}</span></div>
                    <div className="h-2.5 rounded-full bg-[#050B07]">
                      <motion.div key={`l${tent.id}`} initial={{ width: 0 }} animate={{ width: `${(tent.led / tent.hps) * 100}%` }} transition={{ duration: 0.9, ease: EASE }} className="h-full rounded-full bg-[#39FF14]" />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]"><span>HPS</span><span>{`${tent.hps}W`}</span></div>
                    <div className="h-2.5 rounded-full bg-[#050B07]">
                      <div className="h-full w-full rounded-full bg-red-400/60" />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Power bill / month</div>
                <div className="mt-2 font-display text-3xl font-black text-[#39FF14]" data-testid="savings-monthly">
                  {money(Math.round(saveMo))}
                  <span className="ml-1 text-sm font-semibold text-[#86EFAC]">saved</span>
                </div>
                <div className="mt-1 text-xs text-[#9FB8A8]">
                  {`${money(Math.round(monthly(tent.led)))} vs ${money(Math.round(monthly(tent.hps)))} · ${money(Math.round(saveMo * 12))}/yr`}
                </div>
              </div>
              <div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Yield lift</div>
                <div className="font-display text-3xl font-black text-[#F0FDF4]" data-testid="savings-yield-boost">
                  {`+${tent.boost}%`}
                </div>
                <div className="mt-1 text-xs text-[#9FB8A8]">typical grams-per-harvest improvement over HPS</div>
              </div>
            </div>
            <Link
              to={`/product/${tent.slug}`}
              data-testid="savings-view-light"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-[#39FF14]/40 px-6 py-3 font-display text-sm font-bold text-[#39FF14] transition-all hover:bg-[#39FF14] hover:text-[#050B07]"
            >
              {`Meet the ${tent.light.split("·")[0].trim()}`}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState(() => window.__INITIAL_DATA__?.products?.filter((p) => p.featured).slice(0, 4) || []);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.featured).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="SuperTechLED — LED Grow Lights Australia | Home Growers & Commercial Use"
        description="High-performance Samsung & Cree diode LED grow lights designed in Australia. Quantum boards, bar lights and COB fixtures with free express shipping AU-wide and a 2-year warranty."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "SuperTechLED",
          url: SITE_URL + "/",
          logo: SITE_URL + "/img/brand/neon-sign.jpeg",
          email: "supertechled@outlook.com",
          description:
            "Australian designer of high-performance LED grow lights for home growers and commercial use. Genuine Samsung & Cree diodes, Meanwell drivers, free express shipping AU-wide and a 2-year local warranty.",
          address: { "@type": "PostalAddress", addressCountry: "AU" },
          sameAs: [],
        }}
      />
      <Hero />
      <Marquee />
      <section id="range" className="mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="featured-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionTag>The fleet</SectionTag>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
              Grower favourites
            </h2>
          </div>
          <Link to="/shop" data-testid="featured-view-all" className="group flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#39FF14]">
            View the full range
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.slug} p={p} i={i} />
          ))}
        </div>
      </section>
      <Manifesto />
      <BrandWall />
      <SpectrumSection />
      <Savings />
      <section className="border-t border-[#39FF14]/10 bg-[#071009]">
        <div className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8">
          <Reveal>
            <Truck className="mx-auto h-8 w-8 text-[#39FF14]" />
            <h2 className="font-display mx-auto mt-6 max-w-2xl text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl">
              Free express shipping, anywhere in Australia
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-[#9FB8A8] sm:text-base">
              Every light ships free and fast from local stock, backed by a 2-year Australian warranty and a
              30-day no-drama returns policy.
            </p>
            <Link
              to="/shop"
              data-testid="cta-shop-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-8 py-4 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_40px_rgba(57,255,20,0.5)]"
            >
              Start growing <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
